/**
 * Fixed series colour per water parameter (#1653): the design system's
 * data-series tokens (color.chart.*, ≥ 3:1 on every background in both
 * themes — tokens/__tests__/contrast.test.ts). The same parameter gets the
 * same colour in its chart line, legend swatch and table rows. Colour is never
 * the only cue: legend, headings and table always name the parameter.
 */

/** The chart tokens, `--color-chart-<slot>`. */
export const SERIES_COLOUR_SLOTS = ['kh', 'no3', 'po4', 'ca', 'mg'] as const;
export type SeriesColourSlot = (typeof SERIES_COLOUR_SLOTS)[number];

/** Parameters with their own token keep it whatever else is selected. */
const FIXED_SLOT: Readonly<Record<string, SeriesColourSlot>> = {
  alkalinity: 'kh',
  nitrate: 'no3',
  phosphate: 'po4',
  calcium: 'ca',
  magnesium: 'mg',
};

/** CSS custom property of a slot, e.g. `--color-chart-no3`. */
export function seriesColourVar(slot: SeriesColourSlot): string {
  return `--color-chart-${slot}`;
}

/** Class of the legend / table swatch (ParameterSwatch.css). */
export function seriesColourClass(slot: SeriesColourSlot): string {
  return `parameter-swatch--${slot}`;
}

function hashSlot(parameterId: string): number {
  let hash = 0;
  for (let i = 0; i < parameterId.length; i += 1) hash = (hash * 31 + parameterId.charCodeAt(i)) >>> 0;
  return hash % SERIES_COLOUR_SLOTS.length;
}

/**
 * Colour slot per selected parameter. Parameters with a fixed token keep it;
 * every other parameter gets a stable hash slot, moved to the next free one
 * when it collides — so up to five parameters (the view shows at most three)
 * always get distinct colours, independent of the selection order.
 */
export function assignSeriesColours(parameterIds: readonly string[]): Map<string, SeriesColourSlot> {
  const result = new Map<string, SeriesColourSlot>();
  const taken = new Set<SeriesColourSlot>();
  const unique = [...new Set(parameterIds)];
  for (const id of unique) {
    const fixed = FIXED_SLOT[id];
    if (fixed) {
      result.set(id, fixed);
      taken.add(fixed);
    }
  }
  for (const id of unique.filter((p) => !FIXED_SLOT[p]).sort()) {
    let index = hashSlot(id);
    for (let tries = 0; tries < SERIES_COLOUR_SLOTS.length && taken.has(SERIES_COLOUR_SLOTS[index]!); tries += 1) {
      index = (index + 1) % SERIES_COLOUR_SLOTS.length;
    }
    const slot = SERIES_COLOUR_SLOTS[index]!;
    result.set(id, slot);
    taken.add(slot);
  }
  return result;
}
